import { useEffect, useState } from 'react'
import ModalMainWrapper from './ModalMainWrapper'
import ModalContentWrapper from './ModalContentWrapper'
import ModalHeader from './ModalHeader'
import ModalContent from './ModalContent'
import { CSV } from '../../helpers/csvparse'

export default function CsvModal({ title, name, url, maxRows = 200 }) {
    const modalTitle = title || name || 'CSV Preview'
    const [headers, setHeaders] = useState([])
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        let isMounted = true

        CSV.parseRemoteFile({
            file: url,
            complete: (results) => {
                if (!isMounted) return
                try {
                    const data = Array.isArray(results?.data)
                        ? results.data
                        : []
                    const computedHeaders =
                        results?.meta?.fields ||
                        (data[0] ? Object.keys(data[0]) : [])
                    setHeaders(computedHeaders)
                    setRows(data.slice(0, maxRows))
                } catch (e) {
                    setError('Failed to parse CSV')
                } finally {
                    setLoading(false)
                }
            },
            error: (err) => {
                if (!isMounted) return
                setError(
                    err?.message ||
                        'Failed to load CSV. Please try opening in a new tab.',
                )
                setLoading(false)
            },
        })

        return () => {
            isMounted = false
        }
    }, [url, maxRows])

    return (
        <ModalMainWrapper>
            <ModalContentWrapper>
                <ModalHeader title={modalTitle} />
                <ModalContent>
                    <div className="w-full">
                        {loading && (
                            <p className="text-sm text-olm-black">Loading...</p>
                        )}
                        {error && (
                            <div className="space-y-2">
                                <p className="text-sm text-red-600">{error}</p>
                                <a
                                    className="underline text-sm"
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    Open in new tab
                                </a>
                            </div>
                        )}
                        {!loading && !error && (
                            <div className="w-full">
                                <div className="overflow-auto max-h-[75vh] border border-slate-200 rounded">
                                    <table className="min-w-full text-left text-sm">
                                        <thead className="bg-slate-50 sticky top-0">
                                            <tr>
                                                {headers.map((h) => (
                                                    <th
                                                        key={h}
                                                        className="px-3 py-2 font-medium text-olm-black border-b border-slate-200">
                                                        {h}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((row, idx) => (
                                                <tr
                                                    key={idx}
                                                    className={
                                                        idx % 2
                                                            ? 'bg-white'
                                                            : 'bg-slate-50/50'
                                                    }>
                                                    {headers.map((h) => (
                                                        <td
                                                            key={`${idx}-${h}`}
                                                            className="px-3 py-2 text-slate-700 border-b border-slate-100">
                                                            {String(
                                                                row?.[h] ?? '',
                                                            )}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="mt-2 flex items-center justify-between">
                                    <p className="text-xs text-slate-500">
                                        Showing {rows.length} rows
                                        {rows.length === maxRows
                                            ? ' (preview)'
                                            : ''}
                                    </p>
                                    <a
                                        className="underline text-sm"
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        Open in new tab
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </ModalContent>
            </ModalContentWrapper>
        </ModalMainWrapper>
    )
}
